import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { theme } from 'antd';
import ReactECharts from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart, LineChart, ScatterChart, RadarChart, TreemapChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent, DataZoomComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { buildOption } from './ChartRenderer';
import HierarchicalTreemap from './HierarchicalTreemap';
import { isHierarchicalDataset } from './reportingDatasetShape';
import { isStatVizType } from './reportingStatCard';
import {
    PRINT_CAPTURE_HEIGHT,
    PRINT_CAPTURE_WIDTH,
} from './reportingPrintUtils';

echarts.use([
    TitleComponent, TooltipComponent, GridComponent, LegendComponent, DataZoomComponent,
    PieChart, BarChart, LineChart, ScatterChart, RadarChart, TreemapChart, CanvasRenderer,
]);

const CHART_STYLE = {
    width: PRINT_CAPTURE_WIDTH,
    height: PRINT_CAPTURE_HEIGHT,
};

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function captureFromRef(chartRef, {
    width = PRINT_CAPTURE_WIDTH,
    height = PRINT_CAPTURE_HEIGHT,
    pixelRatio = 2,
    backgroundColor = '#ffffff',
} = {}) {
    const instance = chartRef?.current?.getEchartsInstance?.();
    if (!instance || typeof instance.getDataURL !== 'function') return null;

    instance.resize({ width, height });
    await wait(500);

    try {
        return instance.getDataURL({
            type: 'png',
            pixelRatio,
            backgroundColor,
        });
    } catch {
        return null;
    }
}

/**
 * Fixed-size off-screen chart used for PDF capture.
 */
const ReportingPrintChartSlot = forwardRef(function ReportingPrintChartSlot({
    dataset,
    vizSpec,
}, ref) {
    const { token } = theme.useToken();
    const chartRef = useRef(null);

    const option = useMemo(() => {
        if (!dataset || isStatVizType(vizSpec)) return null;
        return buildOption(dataset, vizSpec ?? {}, token, { expanded: true });
    }, [dataset, vizSpec, token]);

    const isHierarchicalTreemap = vizSpec?.type === 'treemap' && isHierarchicalDataset(dataset?.columns ?? []);

    useImperativeHandle(ref, () => ({
        captureImage: () => captureFromRef(chartRef, {
            backgroundColor: token.colorBgContainer || '#ffffff',
        }),
    }), [token.colorBgContainer]);

    if (!dataset?.rows?.length || isStatVizType(vizSpec)) return null;

    if (isHierarchicalTreemap) {
        return (
            <div style={CHART_STYLE}>
                <HierarchicalTreemap
                    dataset={dataset}
                    vizSpec={vizSpec}
                    chartRef={chartRef}
                    remountKey="print"
                    style={CHART_STYLE}
                    expanded
                />
            </div>
        );
    }

    if (!option) return null;

    return (
        <div style={CHART_STYLE}>
            <ReactECharts
                ref={chartRef}
                echarts={echarts}
                option={option}
                style={CHART_STYLE}
                notMerge
                lazyUpdate={false}
            />
        </div>
    );
});

export default ReportingPrintChartSlot;
